/**
 * The structure and contact sections of the MD analysis: protein-ligand
 * contacts by residue and over time, the ligand's torsion profiles and the
 * protein's secondary structure. Option and SVG builders live in
 * integrations/mdStructureCharts.js; reports reuse them.
 */
import { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';

import Select from 'BMapsUI/common/Select';
import {
    CONTACTS_HISTOGRAM_CAPTION, CONTACTS_TIMELINE_CAPTION, LIGAND_TORSIONS_CAPTION,
    SECONDARY_STRUCTURE_CAPTION, contactHistogramChartOption, contactRuns,
    contactTimelineChartOption, contactTimelineResidueCount, contactTimelineRuns,
    contactTimelineSize, hasLigandTorsions, ligandTorsionsSvg, secondaryStructureChartOption,
    secondaryStructureRuns, secondaryStructureSize, torsionRuns,
} from 'BMapsSrc/integrations/mdStructureCharts';
import { AnalysisChart } from './LigandAnalysisCharts';

function runChoices(runs, allValue, allLabel) {
    return [
        ...(allValue ? [{ value: allValue, label: runs.length > 1 ? allLabel : 'The run' }] : []),
        ...((runs.length > 1 || !allValue) ? runs.map((r) => ({ value: String(r), label: `Run ${r}` })) : []),
    ];
}

export function ContactsHistogramChart({ compound }) {
    const runs = useMemo(() => contactRuns(compound), [compound]);
    const choices = useMemo(() => runChoices(runs, 'mean', 'Mean of all runs'), [runs]);
    const buildOption = useMemo(() => (run, textColor) => contactHistogramChartOption(
        compound, { run: run === 'mean' ? 'mean' : Number(run), textColor },
    ), [compound]);
    if (runs.length === 0) return null;
    return (
        <AnalysisChart
            id={`md-contacts-${compound.name}`}
            title="Contacts by residue"
            choices={choices}
            buildOption={buildOption}
            height={280}
            caption={CONTACTS_HISTOGRAM_CAPTION}
            ariaLabel={`Protein-ligand contacts of ${compound.name}`}
        />
    );
}

export function ContactsTimelineChart({ compound }) {
    const runs = useMemo(() => contactTimelineRuns(compound), [compound]);
    const choices = useMemo(() => runChoices(runs, null), [runs]);
    const buildOption = useMemo(() => (run, textColor) => contactTimelineChartOption(
        compound, { run: Number(run), textColor },
    ), [compound]);
    if (runs.length === 0) return null;
    const rows = Math.max(...runs.map((r) => contactTimelineResidueCount(compound, r)));
    return (
        <AnalysisChart
            id={`md-ctimeline-${compound.name}`}
            title="Contacts over time"
            choices={choices}
            buildOption={buildOption}
            height={contactTimelineSize(rows, true).height}
            caption={CONTACTS_TIMELINE_CAPTION}
            ariaLabel={`Protein-ligand contacts over time of ${compound.name}`}
        />
    );
}

export function SecondaryStructureChart({ compound }) {
    const runs = useMemo(() => secondaryStructureRuns(compound), [compound]);
    const choices = useMemo(() => runChoices(runs, 'mean', 'Mean of all runs'), [runs]);
    const buildOption = useMemo(() => (run, textColor) => secondaryStructureChartOption(
        compound, { run: run === 'mean' ? 'mean' : Number(run), textColor },
    ), [compound]);
    if (runs.length === 0) return null;
    return (
        <AnalysisChart
            id={`md-sse-${compound.name}`}
            title="Protein secondary structure"
            choices={choices}
            buildOption={buildOption}
            height={secondaryStructureSize(true).height}
            caption={SECONDARY_STRUCTURE_CAPTION}
            ariaLabel={`Protein secondary structure of ${compound.name}`}
        />
    );
}

export function LigandTorsionsPanel({ compound }) {
    const runs = useMemo(() => torsionRuns(compound), [compound]);
    const [run, setRun] = useState('all');
    const svg = useMemo(() => ligandTorsionsSvg(compound, {
        run: run === 'all' ? 'all' : Number(run),
    }), [compound, run]);
    if (!hasLigandTorsions(compound) || !svg) return null;
    const id = `md-torsions-${compound.name}`;
    return (
        <Box id={id} sx={{ marginTop: '0.75rem' }}>
            <Typography component="div" sx={{ fontWeight: 600 }}>
                Ligand torsions
            </Typography>
            {runs.length > 1 && (
                <Select
                    id={`${id}-run`}
                    label="Runs shown"
                    value={run}
                    onChange={(e) => setRun(e.target.value)}
                    sx={{ width: '100%', margin: '0.25rem 0' }}
                >
                    <option value="all">All independent runs</option>
                    {runs.map((r) => <option key={r} value={String(r)}>{`Run ${r}`}</option>)}
                </Select>
            )}
            <Box
                aria-label={`Ligand torsion profiles of ${compound.name}`}
                sx={{ '& svg': { width: '100%', height: 'auto', display: 'block' } }}
                // Built here from the packet's numbers (text escaped) and RDKit's drawing.
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: svg }}
            />
            <Typography variant="caption" component="div" sx={{ opacity: 0.8 }}>
                {LIGAND_TORSIONS_CAPTION}
            </Typography>
        </Box>
    );
}
